import { registerStyles } from "../utils/registerStyles";
// src/popup/SchedulingPopup.js
import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { FaPen, FaPlus, FaSearch, FaTimes, FaTrash, FaUserPlus } from "react-icons/fa";
import { useAuth } from "../contexts/AuthContext";
import LoadingIndicator from "../components/LoadingIndicator";

// Popup-komponent för att hantera scheman (skapa/radera)
function UnassignedEmployees({ employees }) {
  return <section aria-labelledby="unassigned-employees-title" className="scheduling-popup-unassigned">
    <div className="scheduling-popup-unassigned__heading"><FaUserPlus aria-hidden="true" /><div><h3 id="unassigned-employees-title">Personer utan schema</h3><p>{employees.length} personer</p></div></div>
    {employees.length ? <div className="scheduling-popup-unassigned__list">{employees.map((employee) => <div key={employee.id} className="scheduling-popup-unassigned__person">
      <span className="scheduling-popup-unassigned__avatar" aria-hidden="true">{(employee.name || employee.email || "A").trim().charAt(0).toUpperCase()}</span>
      <span><strong>{employee.name || employee.email || "Användare"}</strong>{employee.email && <small>{employee.email}</small>}</span>
    </div>)}</div> : <p className="scheduling-popup-unassigned__empty">Alla användare finns redan i minst ett schema.</p>}
    <div className="scheduling-popup-unassigned__hint"><FaPlus aria-hidden="true" /><span>Klicka på <strong>+</strong> vid ett schema för att lägga till en person i schemat.</span></div>
  </section>;
}

export default function SchedulingPopup({
  isOpen,
  onClose,
  schemas = [],
  allSchemas,
  employees = [],
  loading = false,
  loadError = "",
  onCreateSchema,
  onDeleteSchema,
  onRenameSchema,
  onSaveSchemaEmployees,
}) {
  const { currentUser, companyAccountUid } = useAuth();

  // State för UI
  const [activeTab, setActiveTab] = useState("schemas");
  const [schemaName, setSchemaName] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [editingSchemaId, setEditingSchemaId] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const panelTimer = useRef(null);
  const [employeeDraft, setEmployeeDraft] = useState(null);
  const [employeeDraftDirty, setEmployeeDraftDirty] = useState(false);
  const [savingEmployees, setSavingEmployees] = useState(false);
  const [employeeError, setEmployeeError] = useState("");
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [renamingSchemaId, setRenamingSchemaId] = useState(null);
  const [nameDraft, setNameDraft] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [nameError, setNameError] = useState("");

  // Hämta företagskontots UID (använder companyAccountUid om det finns)
  const accountToUse = companyAccountUid || currentUser?.uid;

  // Återställ state när popup stängs
  useEffect(() => {
    if (!isOpen) {
      clearTimeout(panelTimer.current);
      setActiveTab("schemas");
      setSchemaName("");
      setSaving(false);
      setDeleting(false);
      setDeleteError(null);
      setEditingSchemaId(null);
      setPanelOpen(false);
      setEmployeeDraft(null);
      setEmployeeDraftDirty(false);
      setEmployeeError("");
      setEmployeeSearch("");
      setRenamingSchemaId(null);
      setNameDraft("");
      setNameError("");
    }
  }, [isOpen]);

  useEffect(() => () => clearTimeout(panelTimer.current), []);

  // Kontrollera om det går att skapa schema
  const canCreate = useMemo(() => schemaName.trim().length > 0, [schemaName]);

  // Hantera skapande av nytt schema
  const handleCreate = async () => {
    const trimmedName = schemaName.trim();
    if (!trimmedName || !onCreateSchema) return;

    setSaving(true);

    try {
      const success = await onCreateSchema(trimmedName);

      if (success) {
        setSchemaName("");
        setActiveTab("schemas");
      }
    } catch (error) {
      console.error("Fel vid skapande:", error);
    } finally {
      setSaving(false);
    }
  };

  // Hantera radering av schema
  const handleDelete = async (schemaId) => {
    if (!schemaId || !onDeleteSchema) return;
    
    // Bekräfta radering
    if (!window.confirm("Är du säker på att du vill radera detta schema?")) {
      return;
    }

    setDeleting(true);
    setDeleteError(null);

    try {
      await onDeleteSchema(schemaId);
    } catch (error) {
      console.error("Fel vid radering:", error);
      setDeleteError("Kunde inte radera schemat. Försök igen.");
    } finally {
      setDeleting(false);
    }
  };

  const openEmployeeEditor = (schema) => {
    if (savingEmployees) return;
    clearTimeout(panelTimer.current);
    if (panelOpen) {
      setPanelOpen(false);
      if (editingSchemaId === schema.id) {
        panelTimer.current = setTimeout(() => setEditingSchemaId(null), 320);
        return;
      }
      panelTimer.current = setTimeout(() => openSchemaPanel(schema), 320);
      return;
    }
    openSchemaPanel(schema);
  };

  const openSchemaPanel = (schema) => {
    setEmployeeError("");
    setEmployeeSearch("");
    setEditingSchemaId(schema.id);
    setEmployeeDraft(Array.isArray(schema.visibleEmployeeIds) ? [...schema.visibleEmployeeIds] : []);
    setEmployeeDraftDirty(false);
    panelTimer.current = setTimeout(() => setPanelOpen(true), 20);
  };

  const hideEmployeePanel = useCallback(() => {
    clearTimeout(panelTimer.current);
    setPanelOpen(false);
    panelTimer.current = setTimeout(() => setEditingSchemaId(null), 320);
    setEmployeeSearch("");
    setEmployeeError("");
    setEmployeeDraftDirty(false);
  }, []);

  useEffect(() => {
    if (!editingSchemaId || employeeDraftDirty) return;
    const schema = schemas.find((item) => item.id === editingSchemaId);
    if (!schema) return;
    setEmployeeDraft(Array.isArray(schema.visibleEmployeeIds) ? [...schema.visibleEmployeeIds] : []);
  }, [schemas, editingSchemaId, employeeDraftDirty]);

  const closeEmployeeEditor = () => {
    if (savingEmployees) return;
    hideEmployeePanel();
  };

  useEffect(() => {
    if (!isOpen || !editingSchemaId) return;
    const onKeyDown = (event) => {
      if (event.key === "Escape" && !savingEmployees) {
        event.stopPropagation();
        hideEmployeePanel();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, editingSchemaId, savingEmployees, hideEmployeePanel]);

  const saveEmployees = async () => {
    if (!editingSchemaId || !onSaveSchemaEmployees) return;
    setSavingEmployees(true);
    setEmployeeError("");
    try {
      await onSaveSchemaEmployees(editingSchemaId, employeeDraft || []);
      hideEmployeePanel();
    } catch (error) {
      console.error("Kunde inte spara användare för schemat:", error);
      setEmployeeError("Kunde inte spara användarna. Försök igen.");
    } finally {
      setSavingEmployees(false);
    }
  };

  const saveName = async (event) => {
    event.preventDefault();
    if (!nameDraft.trim() || savingName || !onRenameSchema) return;
    setSavingName(true);
    setNameError("");
    try {
      await onRenameSchema(renamingSchemaId, nameDraft);
      setRenamingSchemaId(null);
    } catch (error) {
      setNameError(error.message || "Kunde inte spara namnet. Försök igen.");
    } finally {
      setSavingName(false);
    }
  };

  // Stäng popup om den inte är öppen
  if (!isOpen) return null;

  const isCreate = activeTab === "create";

  // Stilar för komponenten
  const styles = {
    overlay: {
      position: "fixed",
      inset: 0,
      background: "rgba(17, 24, 39, 0.35)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 2000,
      padding: 16,
    },
    popup: {
      width: "100%",
      maxWidth: 720,
      background: "#ffffff",
      borderRadius: 16,
      boxShadow: "0 12px 30px rgba(0,0,0,0.16)",
      padding: 24,
      boxSizing: "border-box",
      maxHeight: "min(90vh, 850px)",
      overflowY: "auto",
    },
    header: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "0 0 20px 0",
      borderBottom: "1px solid #e5e7eb",
      marginBottom: 24,
    },
    title: {
      fontSize: 18,
      fontWeight: 600,
      color: "#111827",
      margin: 0,
    },
    companyInfo: {
      fontSize: 12,
      color: "#6b7280",
      marginTop: 4,
    },
    closeButton: {
      background: "none",
      border: "none",
      fontSize: 20,
      cursor: "pointer",
      color: "#6b7280",
      padding: 4,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 8,
      transition: "background-color 0.2s",
    },
    tabContainer: {
      display: "flex",
      gap: 24,
      marginBottom: 24,
    },
    tabButton: (active, isCreateTab) => ({
      border: "none",
      background: "transparent",
      padding: "0 6px 12px",
      fontSize: 16,
      fontWeight: active ? 700 : 500,
      color: active ? "#111827" : "#6b7280",
      borderBottom: active
        ? isCreateTab
          ? "3px solid #3b82f6"
          : "3px solid #ef4444"
        : "3px solid transparent",
      cursor: "pointer",
      transition: "all 0.2s",
    }),
    sectionTitle: {
      margin: "0 0 20px",
      fontSize: 22,
      fontWeight: 700,
      color: "#111827",
    },
    label: {
      fontSize: 15,
      color: "#6b7280",
      marginBottom: 8,
      fontWeight: 500,
    },
    input: {
      width: "100%",
      height: 50,
      borderRadius: 10,
      border: "1px solid #d1d5db",
      padding: "0 14px",
      fontSize: 14,
      color: "#111827",
      background: "#ffffff",
      outline: "none",
      boxSizing: "border-box",
      transition: "border-color 0.2s",
    },
    buttonGroup: {
      display: "flex",
      justifyContent: "flex-end",
      gap: 10,
      marginTop: 20,
    },
    cancelButton: {
      height: 42,
      minWidth: 100,
      padding: "0 16px",
      borderRadius: 8,
      border: "1px solid #d1d5db",
      background: "#ffffff",
      color: "#6b7280",
      fontSize: 14,
      fontWeight: 600,
      cursor: "pointer",
      transition: "opacity 0.2s",
    },
    createButton: (disabled) => ({
      height: 42,
      minWidth: 100,
      padding: "0 16px",
      borderRadius: 8,
      border: "1px solid #2563eb",
      background: disabled ? "#93c5fd" : "#3b82f6",
      color: "#ffffff",
      fontSize: 14,
      fontWeight: 600,
      cursor: disabled ? "not-allowed" : "pointer",
      transition: "opacity 0.2s",
    }),
    schemaList: {
      display: "flex",
      flexDirection: "column",
      gap: 10,
      maxHeight: 470,
      overflowY: "auto",
    },
    schemaItem: {
      border: "1px solid #e5e7eb",
      borderRadius: 10,
      background: "#f9fafb",
      padding: "12px 14px",
      display: "block",
    },
    schemaName: {
      fontSize: 15,
      fontWeight: 500,
      color: "#111827",
    },
    deleteButton: (isDeleting) => ({
      height: 36,
      width: 36,
      flexShrink: 0,
      display: "grid",
      placeItems: "center",
      padding: 0,
      border: "1px solid #dc2626",
      borderRadius: 8,
      background: isDeleting ? "#fca5a5" : "#ef4444",
      color: "#ffffff",
      fontSize: 14,
      fontWeight: 600,
      cursor: isDeleting ? "not-allowed" : "pointer",
      transition: "opacity 0.2s",
      opacity: isDeleting ? 0.6 : 1,
    }),
    emptyState: {
      border: "1px solid #e5e7eb",
      borderRadius: 10,
      padding: 14,
      fontSize: 14,
      color: "#6b7280",
      background: "#f9fafb",
    },
    errorMessage: {
      marginTop: 12,
      padding: "10px 14px",
      borderRadius: 8,
      background: "#fee2e2",
      color: "#991b1b",
      fontSize: 14,
      border: "1px solid #fecaca",
    },
  };

  // Hover-effekt för knappar
  const handleButtonHover = (e, isHover, color = null) => {
    if (isHover) {
      e.currentTarget.style.opacity = "0.85";
      if (color) {
        e.currentTarget.style.backgroundColor = color;
      }
    } else {
      e.currentTarget.style.opacity = "1";
      if (color) {
        e.currentTarget.style.backgroundColor = color;
      }
    }
  };

  // Fokus-effekt för input-fält
  const handleInputFocus = (e) => {
    e.target.style.borderColor = "#3b82f6";
  };

  const handleInputBlur = (e) => {
    e.target.style.borderColor = "#d1d5db";
  };

  const selectedEmployeeIds = employeeDraft || [];
  const editingSchema = schemas.find((schema) => schema.id === editingSchemaId);
  const filteredEmployees = employees.filter((employee) =>
    `${employee.name || ""} ${employee.email || ""}`.toLowerCase().includes(employeeSearch.trim().toLowerCase()));
  const companySchemas = allSchemas || schemas;
  // Äldre scheman saknar medlemslista. Visa deras användare här tills de väljs och sparas uttryckligen.
  const assignedEmployeeIds = new Set(companySchemas.flatMap((schema) => schema.visibleEmployeeIds || []));
  const unassignedEmployees = employees.filter((employee) => !assignedEmployeeIds.has(employee.id));

  return (
    <div style={styles.overlay} onClick={panelOpen ? closeEmployeeEditor : onClose}>
      <div data-testid="scheduling-popup-layout" className={`scheduling-popup-layout ${panelOpen ? "is-expanded" : ""}`} onClick={(event) => event.stopPropagation()}>
      <div className="scheduling-popup-main" style={{ ...styles.popup, boxShadow: "none", borderRadius: 0, maxWidth: "none", minWidth: 0 }}>
        {/* Header med Schemaläggning och X-ikon */}
        <div style={styles.header}>
          <div>
            <h3 style={styles.title}>Schemaläggning</h3>
            {accountToUse && (
              <div style={styles.companyInfo}>
    
              </div>
            )}
          </div>
          <button
            style={styles.closeButton}
            onClick={onClose}
            onMouseEnter={(e) => handleButtonHover(e, true)}
            onMouseLeave={(e) => handleButtonHover(e, false)}
          >
            <FaTimes />
          </button>
        </div>

        {/* Tabs */}
        <div style={styles.tabContainer}>
          <button
            type="button"
            onClick={() => setActiveTab("create")}
            disabled={loading || Boolean(loadError)}
            style={styles.tabButton(isCreate, true)}
          >
            Skapa
          </button>

          <button
            type="button"
            onClick={() => setActiveTab("schemas")}
            style={styles.tabButton(!isCreate, false)}
          >
            Scheman
          </button>
        </div>

        {/* Innehåll baserat på aktiv flik */}
        {isCreate ? (
          <div>
            <h2 style={styles.sectionTitle}>Skapa nytt schema</h2>

            <div style={styles.label}>Namn på schemat</div>

            <input
              type="text"
              value={schemaName}
              onChange={(e) => setSchemaName(e.target.value)}
              placeholder="Exempel: schema för lager"
              style={styles.input}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
            />

            <div style={styles.buttonGroup}>
              <button
                type="button"
                onClick={onClose}
                style={styles.cancelButton}
                onMouseEnter={(e) => handleButtonHover(e, true)}
                onMouseLeave={(e) => handleButtonHover(e, false)}
              >
                Avbryt
              </button>

              <button
                type="button"
                onClick={handleCreate}
                disabled={!canCreate || saving || loading || Boolean(loadError)}
                style={styles.createButton(!canCreate || saving || loading || Boolean(loadError))}
                onMouseEnter={(e) => handleButtonHover(e, true)}
                onMouseLeave={(e) => handleButtonHover(e, false)}
              >
                {saving ? "Sparar..." : "Skapa"}
              </button>
            </div>
          </div>
        ) : (
          <div>
            <h2 style={styles.sectionTitle}>Dina scheman</h2>

            <div style={styles.label}>Öppna ett schema med plusknappen och välj vilka användare som ska visas.</div>

            <div style={styles.schemaList}>
              {loadError ? <div role="alert" style={styles.errorMessage}>{loadError}</div> : loading ? (
                <LoadingIndicator message="Laddar scheman och användare..." />
              ) : schemas.length === 0 ? (
                <div style={styles.emptyState}>
                  Det finns inga scheman ännu.
                </div>
              ) : (
                schemas.map((schema) => (
                  <div key={schema.id} style={styles.schemaItem}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 4 }}>
                      <span style={{ ...styles.schemaName, flex: 1 }}>{schema.name}</span>
                      <button type="button" onClick={() => openEmployeeEditor(schema)} disabled={savingEmployees || loading}
                        aria-expanded={panelOpen && editingSchemaId === schema.id} aria-label={`Välj användare för ${schema.name}`}
                        style={{ display: "grid", placeItems: "center", width: 36, height: 36, border: "1px solid #a5b4fc", borderRadius: 8, background: "#eef2ff", color: "#4338ca", cursor: "pointer" }}>
                        <FaPlus aria-hidden="true" />
                      </button>
                      <button type="button" onClick={() => { setRenamingSchemaId((current) => current === schema.id ? null : schema.id); setNameDraft(schema.name); setNameError(""); }}
                        disabled={savingName || deleting || loading || !onRenameSchema}
                        aria-expanded={renamingSchemaId === schema.id}
                        aria-label={`Redigera namn på ${schema.name}`} title="Redigera namn"
                        style={{ display: "grid", placeItems: "center", width: 36, height: 36, flexShrink: 0, border: "1px solid #6323ef", borderRadius: 8, background: "#f4f1ff", color: "#6323ef", cursor: "pointer" }}>
                        <FaPen aria-hidden="true" />
                      </button>
                      <button type="button" onClick={() => handleDelete(schema.id)} disabled={deleting || savingEmployees || loading}
                        aria-label={`Radera ${schema.name}`} title={`Radera ${schema.name}`} aria-busy={deleting}
                        style={styles.deleteButton(deleting)}>
                        <FaTrash aria-hidden="true" />
                      </button>
                    </div>
                    {renamingSchemaId === schema.id && <form onSubmit={saveName} style={{ marginTop: 12 }}>
                      <label htmlFor={`schema-name-${schema.id}`} style={styles.label}>Namn på schemat</label>
                      <input id={`schema-name-${schema.id}`} autoFocus value={nameDraft} disabled={savingName}
                        onChange={(event) => setNameDraft(event.target.value)} style={styles.input}
                        onKeyDown={(event) => { if (event.key === "Escape" && !savingName) { event.stopPropagation(); setRenamingSchemaId(null); } }} />
                      {nameError && <div role="alert" style={styles.errorMessage}>{nameError}</div>}
                      <div style={styles.buttonGroup}>
                        <button type="button" disabled={savingName} onClick={() => setRenamingSchemaId(null)} style={styles.cancelButton}>Avbryt</button>
                        <button type="submit" disabled={savingName || !nameDraft.trim()} style={styles.createButton(savingName || !nameDraft.trim())}>{savingName ? "Sparar..." : "Spara"}</button>
                      </div>
                    </form>}
                  </div>
                ))
              )}
            </div>

            {deleteError && (
              <div style={styles.errorMessage}>
                {deleteError}
              </div>
            )}
          </div>
        )}
      </div>
      <div className="scheduling-popup-panel">
      <div className="scheduling-popup-placeholder" aria-hidden={panelOpen}>
        {loading || loadError ? null : <UnassignedEmployees employees={unassignedEmployees} />}
      </div>
      {editingSchema && <div role="dialog" aria-hidden={!panelOpen} aria-modal="false" aria-labelledby="schema-employees-title" className="scheduling-popup-panel-inner">
          <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 16 }}>
            <div style={{ flex: 1 }}>
              <h2 id="schema-employees-title" style={{ margin: 0, fontSize: 19, color: "#111827" }}>Användare i {editingSchema.name}</h2>
              <p style={{ margin: "6px 0 0", color: "#6b7280", fontSize: 13 }}>Kryssa i användarna som ska visas i schemat.</p>
            </div>
            <button type="button" onClick={closeEmployeeEditor} disabled={savingEmployees} aria-label="Stäng användarval"
              style={{ display: "grid", placeItems: "center", width: 32, height: 32, flexShrink: 0, border: 0, borderRadius: 8, background: "#f3f4f6", color: "#374151", cursor: "pointer" }}><FaTimes aria-hidden="true" /></button>
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 9, padding: "0 12px", border: "1px solid #d1d5db", borderRadius: 9, color: "#6b7280" }}>
            <FaSearch aria-hidden="true" />
            <input autoFocus type="search" aria-label="Sök användare" placeholder="Sök namn eller e-post…" value={employeeSearch}
              onChange={(event) => setEmployeeSearch(event.target.value)} style={{ width: "100%", height: 42, border: 0, outline: 0, fontSize: 14 }} />
          </label>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "12px 0", fontSize: 12, color: "#6b7280" }}>
            <span>{selectedEmployeeIds.filter((id) => employees.some((employee) => employee.id === id)).length} av {employees.length} valda</span>
            <button type="button" onClick={() => { setEmployeeDraft([]); setEmployeeDraftDirty(true); }} disabled={savingEmployees || selectedEmployeeIds.length === 0}
              style={{ border: 0, background: "transparent", color: "#4338ca", fontWeight: 600, cursor: "pointer" }}>Rensa val</button>
          </div>
          <div style={{ flex: 1, minHeight: 80, overflowY: "auto", border: "1px solid #e5e7eb", borderRadius: 10 }}>
            {filteredEmployees.length ? filteredEmployees.map((employee) => <label key={employee.id}
              style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 13px", borderBottom: "1px solid #f3f4f6", background: selectedEmployeeIds.includes(employee.id) ? "#eef2ff" : "#fff", cursor: "pointer" }}>
              <input type="checkbox" checked={selectedEmployeeIds.includes(employee.id)} disabled={savingEmployees}
                onChange={(event) => {
                  const checked = event.target.checked;
                  setEmployeeDraftDirty(true);
                  setEmployeeDraft((current) => {
                    const ids = current || [];
                    return checked ? [...new Set([...ids, employee.id])] : ids.filter((id) => id !== employee.id);
                  });
                }} style={{ width: 16, height: 16, accentColor: "#4f46e5" }} />
              <span style={{ display: "grid", gap: 2, minWidth: 0 }}><strong style={{ color: "#111827", fontSize: 13 }}>{employee.name || "Användare"}</strong>{employee.email && <small style={{ color: "#6b7280", overflowWrap: "anywhere" }}>{employee.email}</small>}</span>
            </label>) : <p style={{ padding: 16, margin: 0, color: "#6b7280", fontSize: 13 }}>{employees.length ? "Ingen användare hittades." : "Inga användare finns ännu."}</p>}
          </div>
          {employeeError && <div role="alert" style={styles.errorMessage}>{employeeError}</div>}
          <div style={styles.buttonGroup}>
            <button type="button" onClick={closeEmployeeEditor} disabled={savingEmployees} style={styles.cancelButton}>Avbryt</button>
            <button type="button" onClick={saveEmployees} disabled={savingEmployees} style={styles.createButton(savingEmployees)}>{savingEmployees ? "Sparar..." : "Spara användare"}</button>
          </div>
        </div>}
      </div>
      </div>
    </div>
  );
}

// Styles live with this component; keep selectors and responsive rules together.
registerStyles("SchedulingPopup", `
.scheduling-popup-layout {
  display: flex;
  position: relative;
  width: min(1120px, calc(100vw - 32px));
  max-height: min(850px, 90vh);
  overflow: hidden;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.16);
}

.scheduling-popup-main {
  flex: 1 1 720px;
  min-width: 0;
  overflow-y: auto;
}

.scheduling-popup-panel {
  position: relative;
  flex: 0 0 400px;
  height: min(650px, 90vh);
  overflow: hidden;
  border-left: 1px solid #e5e7eb;
  background: #f8f9fc;
}

.scheduling-popup-placeholder {
  height: 100%;
  padding: 22px;
  box-sizing: border-box;
  overflow-y: auto;
}

.scheduling-popup-unassigned { display: flex; flex-direction: column; min-height: 100%; }
.scheduling-popup-unassigned__heading { display: flex; align-items: center; gap: 12px; padding-bottom: 16px; border-bottom: 1px solid #e5e7eb; color: #4f46e5; }
.scheduling-popup-unassigned__heading > svg { flex-shrink: 0; width: 38px; height: 38px; padding: 10px; box-sizing: border-box; border-radius: 10px; background: #e8eaff; }
.scheduling-popup-unassigned__heading h3 { margin: 0; color: #111827; font-size: 16px; }
.scheduling-popup-unassigned__heading p { margin: 4px 0 0; color: #6b7280; font-size: 12px; }
.scheduling-popup-unassigned__list { display: grid; align-content: start; gap: 8px; min-height: 0; max-height: 400px; padding: 16px 0; overflow-y: auto; }
.scheduling-popup-unassigned__person { display: flex; align-items: center; gap: 10px; min-width: 0; padding: 10px; border: 1px solid #e5e7eb; border-radius: 9px; background: #fff; }
.scheduling-popup-unassigned__person > span:last-child { display: grid; gap: 2px; min-width: 0; }
.scheduling-popup-unassigned__person strong { color: #1f2937; font-size: 13px; overflow-wrap: anywhere; }
.scheduling-popup-unassigned__person small { color: #6b7280; font-size: 11px; overflow-wrap: anywhere; }
.scheduling-popup-unassigned__avatar { display: grid; place-items: center; width: 32px; height: 32px; flex-shrink: 0; border-radius: 50%; background: #ede9fe; color: #6d28d9; font-size: 12px; font-weight: 700; }
.scheduling-popup-unassigned__empty { margin: 18px 0; color: #6b7280; font-size: 13px; line-height: 1.5; }
.scheduling-popup-unassigned__hint { display: flex; align-items: flex-start; gap: 10px; margin-top: auto; padding: 13px; border: 1px solid #c7d2fe; border-radius: 10px; background: #eef2ff; color: #4338ca; font-size: 12px; line-height: 1.5; }
.scheduling-popup-unassigned__hint svg { flex-shrink: 0; margin-top: 2px; }
.scheduling-popup-unassigned__hint strong { font-size: 14px; }
.scheduling-popup-layout.is-expanded .scheduling-popup-placeholder { visibility: hidden; }

@media (max-width: 850px) {
  .scheduling-popup-placeholder { display: none; }
}

.scheduling-popup-panel-inner {
  display: flex;
  flex-direction: column;
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  min-height: 0;
  padding: 22px;
  box-sizing: border-box;
  background: #fff;
  opacity: 0;
  transform: translateX(-100%);
  pointer-events: none;
  transition: transform 320ms cubic-bezier(.22, 1, .36, 1), opacity 240ms ease;
}

.scheduling-popup-layout.is-expanded .scheduling-popup-panel-inner {
  opacity: 1;
  transform: translateX(0);
  pointer-events: auto;
}

@media (max-width: 850px) {
  .scheduling-popup-layout {
    width: min(720px, calc(100vw - 32px));
    min-height: min(600px, 90vh);
  }

  .scheduling-popup-panel {
    position: absolute;
    inset: 0 0 0 auto;
    width: min(420px, 100%);
    height: 100%;
    border-left-width: 0;
    background: transparent;
    pointer-events: none;
  }

  .scheduling-popup-placeholder {
    display: none;
  }

  .scheduling-popup-layout.is-expanded .scheduling-popup-panel {
    pointer-events: auto;
  }

  .scheduling-popup-panel-inner {
    border-left: 1px solid #e5e7eb;
  }
}

@media (max-width: 500px) {
  .scheduling-popup-panel {
    width: 100%;
  }
}

@media (prefers-reduced-motion: reduce) {
  .scheduling-popup-panel-inner {
    transition: none;
  }
}
`);
